import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { IconClose } from '@/icons'
import { Button } from './Button'

/**
 * The one dialog system (best practices §1.6).
 *
 * Built on `<dialog>` so focus trapping, the top layer and inertness of the page behind come
 * from the platform rather than from a hand-rolled key handler that will eventually be wrong.
 */

export interface DialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description?: string | undefined
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** Blocks dismissal by Escape and backdrop click. For a dialog with work in flight. */
  isDismissable?: boolean
}

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' } as const

export function Dialog({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  isDismissable = true,
}: DialogProps) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const descriptionId = useId()

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (isOpen && !dialog.open) {
      // showModal, not show: only the modal form traps focus and makes the rest inert.
      dialog.showModal()
    } else if (!isOpen && dialog.open) {
      dialog.close()
    }
  }, [isOpen])

  // The platform fires `cancel` for Escape. Preventing it is how a busy dialog stays open.
  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      event.preventDefault()
      if (isDismissable) onClose()
    },
    [isDismissable, onClose],
  )

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      if (!isDismissable) return
      // A click lands on the dialog element itself only when it hit the backdrop; anything
      // inside the panel has the panel as its target.
      if (event.target === ref.current) onClose()
    },
    [isDismissable, onClose],
  )

  return (
    <dialog
      ref={ref}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      className={cn(
        'w-[calc(100vw-2rem)] rounded-card border border-border bg-overlay p-0 text-text shadow-overlay',
        'backdrop:bg-scrim open:motion-safe:animate-in open:motion-safe:fade-in',
        'm-auto',
        SIZES[size],
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
        <div className="min-w-0">
          <h2 id={titleId} className="text-[0.9375rem] leading-6 font-semibold">
            {title}
          </h2>
          {description ? (
            <p id={descriptionId} className="mt-1 text-[0.8125rem] leading-5 text-text-secondary">
              {description}
            </p>
          ) : null}
        </div>
        {isDismissable ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="-m-1 shrink-0 rounded p-1 text-text-muted transition-colors hover:text-text"
          >
            <IconClose size={16} />
          </button>
        ) : null}
      </div>

      {children ? <div className="max-h-[60vh] overflow-y-auto px-5 py-4">{children}</div> : null}

      {footer ? (
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3.5">
          {footer}
        </div>
      ) : null}
    </dialog>
  )
}

export interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  /** Say what will happen and what cannot be undone (best practices §1.16). */
  body: string
  confirmLabel: string
  cancelLabel: string
  /** Styles the confirm control as destructive and is the default for anything irreversible. */
  isDestructive?: boolean
  isPending?: boolean
  children?: ReactNode
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  cancelLabel,
  isDestructive = true,
  isPending = false,
  children,
}: ConfirmDialogProps) {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      isDismissable={!isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant={isDestructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            isLoading={isPending}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-[0.8125rem] leading-5 text-text-secondary">{body}</p>
      {children ? <div className="mt-4">{children}</div> : null}
    </Dialog>
  )
}
